import type { JSX } from "react";
import { redirect } from "react-router";
import { PLAYNC_DOMAIN } from "@core/constants/service";
import { networkType } from "@core/types/uiType";
import DefaultLayout from "@core/layouts/DefaultLayout";
import SimpleLayout from "@core/layouts/SimpleLayout";
import SubPageLayout from "@core/layouts/SubPageLayout";
import ServiceLayout from "@core/layouts/ServiceLayout";
import ErrorPage from "@core/layouts/error/ErrorPage";
import AuthGuard from "@features/auth/component/AuthGuard";
import { oldPathRedirect } from "@core/router/oldPathRedirect";
import { datePageParam100DaysValidateLoader, datePageParamValidateLoader, ipDatePagingParamValidateLoader } from "@core/router/loader/searchParamsValidateLoader";
import ImportChunkError from "@core/layouts/error/custom/ImportChunkError";

function safeLazy(importer: () => Promise<{ Component: () => JSX.Element }>) {
  let retried = false;

  return async () => {
    try {
      return await importer();
    } catch (error) {
      if (!retried) {
        retried = true;
        window.location.reload();
        return;
      }

      throw new ImportChunkError("ImportChunkError: " + error);
    }
  };
}

/**
 * router
 * - Component는 lazy()를 이용해 동적 import 하도록 설정
 * - AuthGuard 컴포넌트 내부에서 로그인 정보 조회 쿼리 실행 후 권한 체크 로직 수행하므로, Component는 AuthGuard의 children으로 설정해야 함
 */
export const routes = (network: networkType) => [
  {
    path: "/",
    Component: DefaultLayout,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        handle: {
          title: "NC Family ZONE-NCSOFT",
        },
        lazy: safeLazy(async () => {
          const { default: Component } = await import("@features/main/pages/Main");

          return {
            Component: () => (
              <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_1", "PLAYNC_USER_2_ALL"]}>
                <Component />
              </AuthGuard>
            ),
          };
        }),
      },
      {
        path: "/management",
        Component: SubPageLayout,
        children: [
          {
            path: "ip-setting",
            handle: {
              title: "IP 실시간 관리 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/management/pages/IpSetting");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "ip-history",
            handle: {
              title: "IP 변경 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/management/pages/IpHistory");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
            loader: ipDatePagingParamValidateLoader,
          },
        ],
      },
      {
        path: "/product",
        Component: SubPageLayout,
        children: [
          // G코인 관리_이용 내역: 충전 내역
          {
            path: "history/charge",
            handle: {
              title: "충전내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/History/Charging");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
            loader: datePageParamValidateLoader,
          },
          // G코인 관리_이용 내역: 소진 내역
          {
            path: "history/usage",
            handle: {
              title: "소진내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/History/Usage");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
            loader: datePageParam100DaysValidateLoader,
          },
          // G코인 관리_세금계산서
          {
            path: "tax",
            handle: {
              title: "세금 계산서 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/Tax");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
            loader: datePageParamValidateLoader,
          },
          // G코인 관리_환불: 환불신청
          {
            path: "refund/apply",
            handle: {
              title: "환불 신청 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/Refund/Application");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          // G코인 관리_환불: 환불 내역
          {
            path: "refund/history",
            handle: {
              title: "환불 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/Refund/History");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
            loader: datePageParamValidateLoader,
          },
          // G코인 관리_쿠폰
          {
            path: "coupons",
            handle: {
              title: "쿠폰 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinManage/Coupon");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          // G코인 충전_G코인 충전 안내
          {
            path: "charge/intro-and-charge",
            handle: {
              title: "충전 안내 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinCharge/IntroAndPayment");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_2_ALL"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          // G코인 충전_G코인 충전 팝업
          {
            path: "charge/payment",
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinCharge/IntroAndPayment/PaymentDialog");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          // G코인 충전_게임 콘텐츠 소개
          {
            path: "charge/aboutgame-and-advantage",
            handle: {
              title: "콘텐츠 소개 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/product/pages/GCoinCharge/AboutgameAndAdvantage");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_1", "PLAYNC_USER_2_ALL"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
        ],
      },
      {
        path: "/board",
        Component: SubPageLayout,
        children: [
          {
            path: "notice",
            handle: {
              title: "공지사항 : NC Family ZONE-NCSOFT",
            },
            children: [
              {
                path: "list",
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/helpdesk/pages/Notice/List");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["PUBLIC"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
              {
                path: "view",
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/helpdesk/pages/Notice/View");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["PUBLIC"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
            ],
          },
          {
            path: "faq",
            handle: {
              title: "FAQ : NC Family ZONE-NCSOFT",
            },
            children: [
              {
                path: "list",
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/helpdesk/pages/Faq/List");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_1", "PLAYNC_USER_2_ALL"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
              {
                path: "view",
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/helpdesk/pages/Faq/View");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_1", "PLAYNC_USER_2_ALL"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
            ],
          },
        ],
      },
      {
        path: "/helpdesk",
        Component: SubPageLayout,
        children: [
          {
            path: "contact",
            handle: {
              title: "문의하기 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Contact/Contact");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "contact/list",
            handle: {
              title: "내 문의 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Contact/List");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2_ALL"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "contact/view/:id",
            handle: {
              title: "내 문의 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Contact/View");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2_ALL"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "contact/write",
            handle: {
              title: "문의하기 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Contact/Write");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "contact/complete",
            handle: {
              title: "문의하기 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Contact/Complete");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "report",
            handle: {
              title: "미납자 신고 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Report/List");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "report/view/:id",
            handle: {
              title: "미납자 신고 내역 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Report/View");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "remote",
            handle: {
              title: "원격지원 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/helpdesk/pages/Remote");

              return {
                Component: () => (
                  <AuthGuard requiredAuth={["LOGOUT_USER", "PLAYNC_USER_1", "PLAYNC_USER_2_ALL"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
        ],
      },
      {
        path: "/account",
        children: [
          {
            element: <SubPageLayout />,
            children: [
              {
                path: "user",
                handle: {
                  title: "회원정보 관리 : NC Family ZONE-NCSOFT",
                },
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/account/pages/UserAccount");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
              {
                path: "cafe",
                handle: {
                  title: "PC방 정보 관리 : NC Family ZONE-NCSOFT",
                },
                lazy: safeLazy(async () => {
                  const { default: Component } = await import("@features/account/pages/UserICafe");
                  return {
                    Component: () => (
                      <AuthGuard requiredAuth={["PLAYNC_USER_2"]}>
                        <Component />
                      </AuthGuard>
                    ),
                  };
                }),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/account",
    errorElement: <ErrorPage />,
    children: [
      {
        element: <SimpleLayout />,
        children: [
          {
            path: "register",
            handle: {
              title: "회원가입 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/account/pages/RegisterAccount");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "find-id",
            handle: {
              title: "아이디 찾기 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/account/pages/FindId/index");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "find-password",
            handle: {
              title: "비밀번호 찾기 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/account/pages/FindPassword/index");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "change-password",
            handle: {
              title: "비밀번호 변경 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/account/pages/ResetPassword/index");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
        ],
      },
    ],
  },
  {
    path: "/search",
    errorElement: <ErrorPage />,
    handle: {
      title: "PC방 찾기 : NC Family ZONE-NCSOFT",
    },
    children: [
      {
        path: "map",
        lazy: safeLazy(async () => {
          const { default: Component } = await import("@features/search/pages/MapSearch");
          return {
            Component: () => (
              <AuthGuard requiredAuth={["PUBLIC"]}>
                <Component />
              </AuthGuard>
            ),
          };
        }),
      },
    ],
  },
  {
    path: "/service",
    errorElement: <ErrorPage />,
    children: [
      {
        element: <ServiceLayout />,
        children: [
          {
            path: "premium",
            handle: {
              title: "멀티 프리미엄 서비스 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/service/pages/Premium");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "partner",
            handle: {
              title: "파트너 PC방 프로그램 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/service/pages/Partner");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
        ],
      },
    ],
  },
  {
    path: "/promotion",
    errorElement: <ErrorPage />,
    children: [
      {
        element: <SimpleLayout usePadding={false} />,
        children: [
          {
            path: "aion2-20251015",
            handle: {
              title: "AION2 출시 기념 홍보물 배포 안내 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/promotion/pages/aion2/20251015/Page");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
          {
            path: "lineageclassic-20260107",
            handle: {
              title: "엘라라 출시 기념 홍보물 배포 안내 : NC Family ZONE-NCSOFT",
            },
            lazy: safeLazy(async () => {
              const { default: Component } = await import("@features/promotion/pages/lineageclassic/20260107/Page");
              return {
                Component: () => (
                  <AuthGuard requiredAuth={["PUBLIC"]}>
                    <Component />
                  </AuthGuard>
                ),
              };
            }),
          },
        ],
      },
    ],
  },

  //== 기존 PC방 URL Redirect (추후 제거 예정) ==//
  ...oldPathRedirect,
  //== 기존 PC방 URL Redirect (추후 제거 예정) ==//

  {
    path: "*",
    loader: () => redirect(`${PLAYNC_DOMAIN[network]}/error/404`),
  },
];
